import { extend } from 'flarum/common/extend';
import Page from 'flarum/common/components/Page';

/*
 * Eigen, lichte bezoekersregistratie.
 *
 * Eén POST per pageview en per externe click, via sendBeacon: de browser stuurt
 * die buiten de renderlus om en blokkeert dus nooit een pagina of een link. Er
 * worden geen cookies gezet en er gaat niets naar een derde partij.
 *
 * Een pageview vertrekt pas na een teken van een mens: een muisbeweging, een tik,
 * een toets of het scrollwiel. Crawlers die de pagina in een headless browser
 * renderen, voeren dit script ook uit en kwamen zo in de cijfers (oktober 2026);
 * die bewegen niets. Tot dan wachten de pageviews in een rij.
 *
 * Sommige crawlers bootsen wel muisbewegingen na, maar verlaten de pagina binnen
 * twee seconden. Daarom ook een minimale leestijd voor het eerste bezoek: pas na
 * MIN_DWELL_MS op de site vertrekt de eerste pageview. Wie daarna doorklikt,
 * wordt meteen geteld.
 */

const ENDPOINT = '/lc-collect';

/** Minimale tijd tussen twee registraties van hetzelfde pad (ms). */
const DEDUPE_MS = 2000;

/** Gebeurtenissen die alleen een mens (of een echte invoer) veroorzaakt. */
const HUMAN_EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'wheel'];

/** Minimale tijd op de site voor de eerste pageview vertrekt (ms). */
const MIN_DWELL_MS = 5000;

/** Maximaal aantal pageviews dat op een eerste interactie wacht. */
const MAX_PENDING = 5;

let lastPath = null;
let lastTime = 0;
let sentAny = false;
let human = false;
let ready = false;
let pending = [];

/** Stuurt de payload weg zonder op een antwoord te wachten. */
function send(payload) {
  const body = JSON.stringify(payload);

  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }

    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {});
  } catch (e) {
    // Statistieken mogen nooit een fout op de site veroorzaken.
  }
}

function trackPageview() {
  const path = window.location.pathname;
  const now = Date.now();

  if (path === lastPath && now - lastTime < DEDUPE_MS) return;

  // Bij de eerste pageview is document.referrer de echte herkomst; daarna is het
  // navigatie binnen de site, wat de server als intern herkent.
  const referrer = sentAny ? window.location.origin + lastPath : document.referrer;

  lastPath = path;
  lastTime = now;
  sentAny = true;

  const payload = {
    e: 'pv',
    p: path,
    q: window.location.search,
    r: referrer || '',
    t: document.title || '',
    h: 1,
  };

  if (ready) {
    send(payload);
  } else if (pending.length < MAX_PENDING) {
    pending.push(payload);
  }
}

/** Interactie gezien én lang genoeg op de site: wachtende pageviews versturen. */
function flush() {
  if (ready || !human) return;

  const wait = MIN_DWELL_MS - performance.now();

  if (wait > 0) {
    setTimeout(flush, wait);
    return;
  }

  ready = true;
  pending.forEach(send);
  pending = [];
}

/** Eerste echte interactie. */
function markHuman(event) {
  if (human || !event.isTrusted) return;

  human = true;
  HUMAN_EVENTS.forEach((type) => window.removeEventListener(type, markHuman, true));

  flush();
}

/** Is dit een link naar een andere website? */
function isOutbound(link) {
  if (!link || !link.href) return false;
  if (!/^https?:$/.test(link.protocol)) return false;
  if (link.hasAttribute('download')) return false;

  return link.host !== window.location.host;
}

function trackClick(event) {
  if (!event.isTrusted) return;

  // Alleen gewone clicks en middenclicks; rechtsklikken opent niets.
  if (event.button !== undefined && event.button > 1) return;

  const link = event.target && event.target.closest ? event.target.closest('a[href]') : null;

  if (!isOutbound(link)) return;

  send({
    e: 'click',
    p: window.location.pathname,
    u: link.href,
    x: (link.textContent || link.getAttribute('aria-label') || '').trim().slice(0, 160),
  });
}

export default function trackVisits() {
  // Een browser die zelf zegt dat hij op afstand bestuurd wordt, telt niet mee.
  if (navigator.webdriver) return;

  HUMAN_EVENTS.forEach((type) => window.addEventListener(type, markHuman, { capture: true, passive: true }));

  // Elke Flarum-pagina (ook bij navigatie binnen de app) meldt zich hier aan.
  // setTimeout zodat de paginatitel al gezet is.
  extend(Page.prototype, 'oncreate', () => {
    setTimeout(trackPageview, 0);
  });

  // Vangnet voor pagina's die oncreate overschrijven zonder super aan te roepen.
  setTimeout(trackPageview, 1500);

  document.addEventListener('click', trackClick, true);
  document.addEventListener('auxclick', trackClick, true);
}
